import { Box, Grid, Stack } from '@mantine/core';
import { COMMENT_PLACEHOLDER_ID } from '@repo/secoda-editor/marks/Comment';
import { removeTrailingWhitespaces } from '@repo/secoda-editor/utils/removeTrailingWhitespaces';
import { useAuthUser, type IComment } from '../../../api';
import { UserAvatar } from '../../../components/UserAvatar';
import type { WithOnlyIdRequired } from '../../../lib/typescript';
import Comment from '../components/Comment';
import CommentForm from '../components/CommentForm';
import { useCommentStyles } from '../components/styles';
import { useCommentStoreContext } from '../hooks';

export type IFixedCommentThreadProps = {
	comments?: IComment[];
	rootCommentID: string;
	focusedId?: string;
	onCreateComment: (data: Partial<Omit<IComment, 'id'>>) => Promise<IComment>;
	onUpdateComment: (data: WithOnlyIdRequired<IComment>) => Promise<void>;
	onDeleteComment: (id: string) => Promise<void>;
	deletedOwner?: {
		id: string;
		display_name: string;
		profile_picture?: string;
	} | null;
	deletedAt?: string;
};

function FixedCommentThread({
	comments,
	rootCommentID,
	focusedId,
	onCreateComment,
	onUpdateComment,
	onDeleteComment,
	// deletedOwnerName is intentionally not destructured to avoid unused var warning
	deletedOwner,
	deletedAt,
}: IFixedCommentThreadProps) {
	const { user } = useAuthUser();
	const { theme, classes } = useCommentStyles();

	const {
		focusedCommentID,
		selectedText,
		setPlaceholderVisible,
		setSelectedText,
		setFocusedCommentId,
		handleProseMirrorRemoveComment,
		handleProseMirrorReplacePlaceholder,
	} = useCommentStoreContext();
	const isFocused = focusedCommentID === rootCommentID;
	const isPlaceholder = rootCommentID === COMMENT_PLACEHOLDER_ID;

	const handleOnClick = () => {
		setFocusedCommentId(rootCommentID);
	};

	const handleSubmitComment = async (commentDefinition: string) => {
		const newComment = await onCreateComment({
			root: isPlaceholder ? undefined : rootCommentID,
			quoted_text: isPlaceholder ? selectedText : '',
			definition: removeTrailingWhitespaces(commentDefinition),
		});

		if (isPlaceholder) {
			setSelectedText('');
			handleProseMirrorReplacePlaceholder(newComment.id);
		}
	};

	const handleCancelComment = () => {
		setFocusedCommentId(undefined);
		if (isPlaceholder) {
			setPlaceholderVisible(false);
			handleProseMirrorRemoveComment(COMMENT_PLACEHOLDER_ID);
		}
	};

	const isFocusedMissing =
		!!focusedId && !(comments || []).some((c) => c.id === focusedId);

	// Build a synthetic comment that mirrors the real comment UI
	const syntheticDeletedComment: IComment | undefined =
		isFocusedMissing && deletedOwner
			? (() => {
					const syntheticOwner = {
						id: deletedOwner.id,
						display_name: deletedOwner.display_name,
						profile_picture: deletedOwner.profile_picture,
					};

					return {
						id: focusedId || `${rootCommentID}-deleted`,
						created_at: deletedAt || new Date().toISOString(),
						updated_at: deletedAt || new Date().toISOString(),
						owner: syntheticOwner as unknown as IComment['owner'],
						resolved: false,
						quoted_text: '',
						definition: 'Deleted',
						edited_at: deletedAt || undefined,
						reactions: [],
						root: rootCommentID,
						entity: undefined,
						data_access_request: undefined,
						incident: undefined,
					} as unknown as IComment;
				})()
			: undefined;

	const isThreadHighlighted =
		isFocused ||
		(!!focusedId && (comments || []).some((c) => c.id === focusedId)) ||
		Boolean(syntheticDeletedComment);

	return (
		<Box onClick={handleOnClick}>
			<Stack spacing={0}>
				{syntheticDeletedComment ? (
					<Comment
						key={`deleted-${syntheticDeletedComment.id}`}
						comment={syntheticDeletedComment}
						showQuotedText
						onUpdateComment={onUpdateComment}
						onDeleteComment={onDeleteComment}
						titleMaxWidth={220}
						showResolveAction={false}
						highlighted
					/>
				) : null}
				{comments?.map((comment, idx) => (
					<Comment
						key={`${comment.id}`}
						comment={comment}
						showQuotedText={idx === 0}
						onUpdateComment={onUpdateComment}
						onDeleteComment={onDeleteComment}
						titleMaxWidth={220}
						showResolveAction
						highlighted={comment.id === focusedId}
					/>
				))}
			</Stack>
			{!syntheticDeletedComment && (
				<Grid
					pt={theme.spacing.xs}
					className={
						isThreadHighlighted ? classes.replyContainerHighlighted : undefined
					}
				>
					<Grid.Col span={2} className={classes.commentAvatar}>
						{user && <UserAvatar user={user} size="xxs" />}
					</Grid.Col>
					<Grid.Col span="auto" className={classes.commentContent}>
						<CommentForm
							key="reply-box-fixed"
							placeholder="Leave a reply..."
							autoFocus={isFocused}
							onSubmit={handleSubmitComment}
							onCancel={handleCancelComment}
						/>
					</Grid.Col>
				</Grid>
			)}
		</Box>
	);
}

export default FixedCommentThread;
